// Feature landing pages (/features/<slug>), one search phrase each. Every line
// must describe something shipped today. No usage numbers or testimonials.

export interface FeaturePage {
  slug: string;
  name: string; // short name for the hub and nav
  title: string;
  description: string;
  keywords: string;
  eyebrow: string;
  h1: string;
  intro: string;
  steps: { title: string; body: string }[];
  details: string[];
  plan: string; // which plan includes it
  related: { label: string; to: string }[];
  faq: { q: string; a: string }[];
  /** No hi/ar/es version: left out of the localized paths and the translated hub. */
  englishOnly?: boolean;
}

export const FEATURE_PAGES: FeaturePage[] = [
  {
    slug: 'mcp-server',
    name: 'MCP server',
    title: 'Fitness Coaching MCP Server: Manage Clients from Claude | TrainZilla',
    description:
      'Connect Claude to your Trainzilla coach account through our remote MCP server. Ask about real clients, draft plans from the real exercise catalog, and approve every change before it saves.',
    keywords: 'fitness mcp server, personal trainer mcp, coaching mcp server, trainerize mcp, claude connector for coaches, ai assistant for personal trainers',
    eyebrow: 'Bring your own AI',
    h1: 'A coaching MCP server: run your client roster from Claude',
    intro:
      'Trainzilla has a remote MCP server, so Claude can read your real clients and act on them: check-ins, habits, plans, sessions and gym data. Every change is shown to you as a preview first and only saved when you confirm.',
    steps: [
      { title: 'Add the connector', body: 'In Claude, open Settings → Customize → Connectors, add a custom connector and enter https://api.tzilla.live/mcp. That URL is the only setup.' },
      { title: 'Sign in to Trainzilla', body: 'Claude sends you to Trainzilla to sign in and approve access (OAuth 2.1 with PKCE). Claude never sees your password and you can revoke access any time.' },
      { title: 'Ask about real clients', body: '"Who is below 70% habit compliance this fortnight and hasn’t checked in this week?" Claude answers from your live account, not a demo.' },
    ],
    details: [
      'Read clients, profiles, habits, check-ins and photo answers, sessions, subscriptions and billing',
      'Draft workout and diet plans and templates from the real Trainzilla exercise and food catalog',
      'Schedule, reschedule and cancel sessions; create check-ins and habits; message a client',
      'Gym tools: classes and rosters, attendance, coaches, locations, prospects and announcements',
      'Calculators for TDEE, macros and one-rep max',
      'Every write returns a preview and needs a second, explicit confirmation',
      'Access is scoped to your own coach account and your role in a gym',
    ],
    plan: 'Available to every Trainzilla coach account.',
    related: [
      { label: 'Connect Trainzilla to Claude (step-by-step guide)', to: '/blog/connect-trainzilla-to-claude' },
      { label: 'The built-in AI Assistant', to: '/features/ai-assistant' },
      { label: 'Trainerize alternative', to: '/alternatives/trainerize' },
    ],
    faq: [
      { q: 'Which AI assistants work with it?', a: 'Claude on claude.ai and in the Claude desktop app, using a custom connector. Any other MCP client that supports remote servers with OAuth sign-in can connect to the same URL.' },
      { q: 'Can Claude change a client’s plan without me knowing?', a: 'No. Every write first returns a preview of exactly what would be created or changed. Nothing is saved until you confirm in the chat.' },
      { q: 'Does it cost extra?', a: 'No. The MCP server is part of your Trainzilla account. You use your own Claude plan.' },
      { q: 'Can it take payments or delete data?', a: 'Taking payments and refunds are not exposed. Deleting plans or templates, like every other write, needs your explicit confirmation.' },
    ],
  },
  {
    slug: 'ai-assistant',
    name: 'AI Assistant',
    title: 'AI Assistant for Personal Trainers, Inside Your Dashboard | TrainZilla',
    description:
      'Ask the Trainzilla AI Assistant about your clients, plans and schedule in plain language. It drafts, books and edits with a preview you approve. Included allowance on Coach Pro.',
    keywords: 'ai assistant for personal trainers, ai for fitness coaches, personal trainer ai, ai coaching assistant',
    eyebrow: 'Built into the coach app',
    h1: 'An AI Assistant that knows your clients',
    intro:
      'Ask in plain language: who missed their check-in, what to change in a stalled client’s plan, or book a session for Thursday. The assistant works on your real data and shows you a preview before it changes anything.',
    steps: [
      { title: 'Ask', body: 'Type a question or a task in the AI Assistant on your Trainzilla dashboard.' },
      { title: 'Review the preview', body: 'For anything that changes data, like a plan edit or a booking, you see exactly what it will do.' },
      { title: 'Approve', body: 'Confirm and it’s saved. Nothing changes until you do.' },
    ],
    details: [
      'Answers about clients, check-ins, habits, plans, sessions and payments',
      'Drafts and edits workout and nutrition plans for you to approve',
      'Books and reschedules sessions inside your working hours',
      'Separate free Help chat for questions about using Trainzilla',
      'Coach Pro includes a monthly AI Assistant allowance; any coach can top up credits',
    ],
    plan: 'Monthly allowance included in Coach Pro. Trial and free coaches can buy credits.',
    related: [
      { label: 'Use Claude instead, via the MCP server', to: '/features/mcp-server' },
      { label: 'How the AI plan agent works', to: '/agent' },
      { label: 'Pricing', to: '/pricing' },
    ],
    faq: [
      { q: 'Is this the same as the AI that drafts weekly plans?', a: 'They work together. The plan agent drafts each client’s next plan from their check-in for you to approve. The AI Assistant is the chat you use to ask questions and give instructions.' },
      { q: 'What happens when the allowance runs out?', a: 'You can top up credits. The free Help chat for questions about using Trainzilla doesn’t use credits.' },
      { q: 'Does it message my clients on its own?', a: 'No. Anything that changes data or contacts a client is shown as a preview for you to confirm.' },
    ],
  },
  {
    slug: 'health-reports',
    name: 'Health reports',
    title: 'InBody, DEXA & Blood Report Reader for Coaches | TrainZilla',
    description:
      'Clients upload an InBody or DEXA scan or a blood report as a photo or PDF and Trainzilla reads the numbers into their record. Smart-scale data syncs from Apple Health and Health Connect.',
    keywords: 'inbody report app, dexa scan tracking, body composition tracking app for coaches, blood report upload fitness app, smart scale coaching app',
    eyebrow: 'Body composition and lab reports',
    h1: 'Turn InBody, DEXA and blood reports into tracked numbers',
    intro:
      'Your client photographs or uploads their report. Trainzilla reads the values and adds them to their record, so body fat, muscle mass and lab markers sit next to their training and nutrition instead of in a PDF nobody opens again.',
    steps: [
      { title: 'Client uploads', body: 'From the client app (or you, from the client’s record on the web), upload a photo or PDF of an InBody, DEXA or other body-composition scan, or a blood report.' },
      { title: 'Values are read', body: 'Every value on the report is extracted and kept, including the ones without a dedicated chart.' },
      { title: 'Track the trend', body: 'Body composition shows on the client’s progress, with separate lines per source: scans, smart scale and manual entries.' },
    ],
    details: [
      'Photo or PDF, including scanned PDFs',
      'InBody, DEXA and other body-composition reports, plus blood reports',
      'Smart scales via Apple Health (iPhone) and Health Connect (Android)',
      'Weight in kg or lbs, chosen by each client',
      'Body-composition card on the client screen for coaches and gym admins',
    ],
    plan: 'Included for every coach and gym; clients use the free client app.',
    related: [
      { label: 'Client app', to: '/client-app' },
      { label: 'Progress tracking', to: '/solutions/progress-tracking' },
      { label: 'Gyms and studios', to: '/gyms' },
    ],
    faq: [
      { q: 'Which smart scales work?', a: 'Any scale whose app writes to Apple Health or Health Connect. The client connects Health once in the Trainzilla app and readings come through.' },
      { q: 'What if a value is read wrong?', a: 'The original report stays attached to the record, so you can check any value against it.' },
      { q: 'Is health data handled carefully?', a: 'Health data is only processed with the client’s consent and they can delete it or disconnect a source at any time. See our privacy policy for where it is stored.' },
    ],
  },
  {
    slug: 'session-packs',
    name: 'Session packs',
    title: 'Session Pack Tracking for Personal Trainers | TrainZilla',
    description:
      'Sell PT memberships with a set number of sessions. See sessions used and left, pause a membership, carry over leftovers and renew by sessions. Bookings follow your working hours.',
    keywords: 'session pack tracking, personal training session tracker, pt session packages software, track client sessions remaining',
    eyebrow: 'In-person and hybrid coaching',
    h1: 'Session packs that track themselves',
    intro:
      'Sell a plan with, say, 12 sessions a month. Trainzilla counts what’s used and what’s left, handles pauses and holidays, and puts members with two or fewer sessions left on your renewals list.',
    steps: [
      { title: 'Set the pack on the plan', body: 'Choose sessions per month or per plan period, whether no-shows count, and whether one month’s leftovers roll over.' },
      { title: 'Book inside your hours', body: 'You or the client book sessions. Bookings and reschedules must fit your working hours and other booked sessions, with split hours if you work mornings and evenings.' },
      { title: 'Renew at the right time', body: 'Used and left are shown to you and the client. Low-session members appear on the renewals list even if their end date is weeks away.' },
    ],
    details: [
      'Sessions per month or per plan period, per plan',
      'No-show and rollover rules per plan',
      'Pause a membership for holidays or injury (up to 90 days)',
      'Split working hours: more than one block of availability per day',
      'Online (video) or in-person sessions',
    ],
    plan: 'Included for every coach and gym.',
    related: [
      { label: 'Gyms and studios', to: '/gyms' },
      { label: 'Client invoices', to: '/features/client-invoices' },
      { label: 'Pricing', to: '/pricing' },
    ],
    faq: [
      { q: 'Do I have to mark each session as done?', a: 'No. A confirmed session in the past counts as attended.' },
      { q: 'What happens to sessions when a member pauses?', a: 'The session month stretches by the pause. For desk memberships the end date moves too; auto-renewing billing dates stay the same.' },
      { q: 'Can clients book outside my hours?', a: 'No. Bookings and reschedules are checked against your working hours and existing sessions.' },
    ],
  },
  {
    slug: 'client-invoices',
    name: 'Client invoices',
    title: 'Automatic Client Invoices for Coaches & Gyms | TrainZilla',
    description:
      'Every client payment gets an invoice automatically: emailed as a PDF and saved in the client’s app. Your business name, optional GSTIN, and cash or UPI desk payments included.',
    keywords: 'invoice software for personal trainers, gym invoice software, automatic client invoices fitness, gst invoice for gym',
    eyebrow: 'Billing',
    h1: 'Client invoices, sent automatically',
    intro:
      'When a client pays you, Trainzilla issues a numbered invoice in your business name, emails it as a PDF and saves it in the client’s app. Cash and UPI payments you record at the desk get one too.',
    steps: [
      { title: 'Add your business details', body: 'Business name and address, and a GSTIN if you have one (optional).' },
      { title: 'Get paid', body: 'Online through your payment link or Trainzilla checkout, or record a cash, UPI or card payment with a photo of the receipt.' },
      { title: 'Invoice goes out', body: 'The client gets the PDF by email and finds it under Transactions in their app. You see every invoice on your sales screen.' },
    ],
    details: [
      'Numbered per seller and financial year',
      'Issued by the gym when the client belongs to a gym, otherwise by the coach',
      'GSTIN optional, for coaches who aren’t GST-registered',
      'Desk payments (cash, UPI, card) with proof of payment',
      'Invoices on the client’s Plan & Renewals tab',
    ],
    plan: 'Included for every coach and gym.',
    related: [
      { label: 'Session packs', to: '/features/session-packs' },
      { label: 'Payment management', to: '/solutions/payment-management' },
      { label: 'Gyms and studios', to: '/gyms' },
    ],
    faq: [
      { q: 'Do I need a GST number?', a: 'No. GSTIN is optional. If you add one, it appears on your invoices.' },
      { q: 'Does Trainzilla take a cut of the payment?', a: 'No. Clients pay you directly and Trainzilla takes no commission; standard payment-gateway fees may apply.' },
      { q: 'Can clients download old invoices?', a: 'Yes. Every invoice stays in the client’s app under Transactions.' },
    ],
  },
  {
    slug: 'gym-check-in-kiosk',
    name: 'Check-in kiosk',
    title: 'Gym Check-in Kiosk with Face & QR Check-in | TrainZilla',
    description:
      'Turn any laptop or tablet into a gym check-in kiosk. Members enrol once with consent and check in with their face and a blink, or show a QR code from their app.',
    keywords: 'gym check in kiosk, face recognition gym check in, qr code gym check in, gym attendance system, gym access software',
    eyebrow: 'For gyms and studios',
    h1: 'A gym check-in kiosk on any laptop or tablet',
    intro:
      'No card readers or register book. Open the kiosk on a device at your door. Members check in with their face, or with a QR code from the Trainzilla app, and the visit lands on their record.',
    steps: [
      { title: 'Pair a device', body: 'Create a kiosk for a location from your gym dashboard and open it on a laptop or tablet with a camera.' },
      { title: 'Members enrol once', body: 'Each member gives consent on screen and enrols their face. Prefer not to? They use the QR code in their app instead.' },
      { title: 'Check in', body: 'Look at the camera and blink, or scan the QR code. Attendance shows on the member’s record and your dashboard.' },
    ],
    details: [
      'Face check-in with a blink check against photos',
      'QR check-in from the member’s Trainzilla app',
      'Stores a numerical face template, not a photo',
      'Members can withdraw consent and have their template deleted',
      'Front desk role that can check members in without seeing money',
    ],
    plan: 'Included in every gym plan (Gym Starter and up).',
    related: [
      { label: 'Gyms and studios', to: '/gyms' },
      { label: 'Session packs', to: '/features/session-packs' },
      { label: 'Gym software in the UAE', to: '/ae' },
    ],
    faq: [
      { q: 'Do I need special hardware?', a: 'No. Any laptop or tablet with a front camera and a browser works.' },
      { q: 'Is face data stored?', a: 'Only a numerical face template, with the member’s explicit consent. It is deleted when the member withdraws consent, leaves the gym, or you turn face check-in off.' },
      { q: 'What if a member doesn’t want face check-in?', a: 'They check in with the QR code in their Trainzilla app.' },
    ],
  },
  {
    slug: 'coaching-community',
    name: 'Client community',
    title: 'Private Community Feed for Coaches & Gyms | TrainZilla',
    description:
      'A private community feed for your clients or gym members inside the Trainzilla app: posts, photos and videos, on web, iPhone and Android. No separate Facebook or WhatsApp group.',
    keywords: 'fitness community app, private community for coaching clients, gym member community app, coaching group app',
    eyebrow: 'Engagement',
    h1: 'A private community for your clients, inside your app',
    intro:
      'Give clients a place to share wins, progress photos and videos with each other and with you, in the same app as their plans, instead of a WhatsApp or Facebook group you have to moderate separately.',
    steps: [
      { title: 'It’s already there', body: 'Every coach has a “My clients” community; gyms get one for their members.' },
      { title: 'Post and reply', body: 'You and your clients post text, photos and videos from web, iPhone or Android.' },
      { title: 'Your brand', body: 'On Coach Pro, the community sits in the app your clients see with your logo and colours.' },
    ],
    details: [
      'Photo and video posts that play inline',
      'Separate communities for your own clients and your gym',
      'Available on the web dashboard and the coach and client apps',
      'Only your clients or members can see it',
    ],
    plan: 'Included for every coach and gym.',
    related: [
      { label: 'Client app', to: '/client-app' },
      { label: 'White label fitness app', to: '/white-label-fitness-app' },
      { label: 'Gyms and studios', to: '/gyms' },
    ],
    faq: [
      { q: 'Can people outside my clients see posts?', a: 'No. Only your clients (or your gym’s members, for a gym community) can see and post.' },
      { q: 'Do clients need another app?', a: 'No. The community is inside the free Trainzilla client app.' },
    ],
  },
  {
    slug: 'pdf-plan-import',
    name: 'PDF plan import',
    englishOnly: true,
    title: 'Import Workout & Meal Plans from PDF into Coaching Software | TrainZilla',
    description:
      'Upload the workout or nutrition plans you already made as PDFs. Trainzilla converts them into editable plans for you to review, or shares the PDF as it is. No retyping when you switch coaching software.',
    keywords: 'import workout plans from pdf, pdf to workout plan, switch coaching software keep plans, upload diet plan pdf for clients, convert pdf training program to app',
    eyebrow: 'Switching software',
    h1: 'Bring your existing PDF plans with you. No retyping.',
    intro:
      'Most coaches have months of workout and meal plans saved as PDFs. Upload one and Trainzilla either turns it into plans you can edit and track, or shares the PDF with the client as it is, so changing software does not mean rebuilding your library.',
    steps: [
      { title: 'Upload the PDF', body: 'Choose a workout or nutrition plan PDF from your computer. Scanned PDFs work too.' },
      { title: 'Convert or share', body: 'Convert it into editable plans that clients can log against, or share the PDF as the plan with nothing to log.' },
      { title: 'Review, then assign', body: 'When you convert, you review the draft and fill in anything the PDF did not say before it is saved. Clients are notified once.' },
    ],
    details: [
      'Works for workout plans and nutrition plans',
      'A training split becomes one workout plan per training day',
      'The conversion only transcribes what is in your PDF. Sets, reps or amounts it cannot find are left for you to fill in, not guessed',
      'Not a plan, or unreadable? Share the PDF as it is instead',
      'Available to every coach, from the web dashboard',
    ],
    plan: 'Included for every coach.',
    related: [
      { label: 'Trainerize alternative', to: '/alternatives/trainerize' },
      { label: 'TrueCoach alternative', to: '/alternatives/truecoach' },
      { label: 'Switch without losing clients', to: '/blog/switch-coaching-software-without-losing-clients' },
      { label: 'Client invoices', to: '/features/client-invoices' },
    ],
    faq: [
      { q: 'Can I import plans from Trainerize, TrueCoach or Everfit?', a: 'If you can save or print a plan as a PDF, you can upload it. We do not connect to those apps directly.' },
      { q: 'Does the conversion invent exercises or numbers?', a: 'No. It copies what is in your PDF. Anything it cannot find stays blank for you to complete in the review step.' },
      { q: 'What does the client see if I share the PDF as it is?', a: 'They get the PDF as their plan. There is nothing to log against, which is why we recommend converting when you want to track progress.' },
      { q: 'Can I upload from the coach mobile apps?', a: 'Not yet. Upload from the web dashboard; the plans then show in the coach and client apps.' },
    ],
  },
  {
    slug: 'pr-tracking',
    name: 'PR tracking',
    englishOnly: true,
    title: 'Track Client PRs and Per-Set Workout Logs | TrainZilla',
    description:
      'Clients log weight and reps for every set. Trainzilla estimates a one-rep max, flags new personal records and sends the coach a digest. Coaches can also log sessions for a client.',
    keywords: 'track client personal records, workout log app for personal trainers, per set weight reps tracking, one rep max tracking for coaches, pr tracking app for coaches',
    eyebrow: 'Strength coaching',
    h1: 'Per-set logging with personal records flagged automatically',
    intro:
      'Clients log the weight and reps of each set. Trainzilla works out an estimated one-rep max, marks a new personal record when a lift beats their best, and gives you the day’s PRs in one notification instead of making you scroll through logs.',
    steps: [
      { title: 'Client logs sets', body: 'In the Train tab, a client enters weight and reps for each set, in kg or lbs.' },
      { title: 'PRs are flagged', body: 'Each lift is compared with the client’s best for that exercise. A heavier weight or a higher estimated one-rep max is a PR.' },
      { title: 'You get a digest', body: 'After a client stops logging for 45 minutes, you get one notification with that day’s PRs.' },
    ],
    details: [
      'Estimated one-rep max uses the Epley formula, for sets of 1 to 12 reps',
      'The first time a lift is logged is the baseline, never a PR',
      'Day Logs on the web dashboard compare actual sets with the plan, with a personal-bests panel',
      'Coaches can log a session for a client; it is tagged “Logged by coach”',
      'Weights show in each client’s own unit, kg or lbs',
    ],
    plan: 'Included for every coach and gym.',
    related: [
      { label: 'Client app', to: '/client-app' },
      { label: 'AI Assistant', to: '/features/ai-assistant' },
      { label: 'Health reports', to: '/features/health-reports' },
      { label: 'For coaches', to: '/for-coaches' },
    ],
    faq: [
      { q: 'Does it track PRs for bodyweight exercises?', a: 'Not yet. Personal records are based on weight lifted and estimated one-rep max.' },
      { q: 'How is a PR decided?', a: 'On our server, against every earlier log of that exercise for that client, so the web dashboard and the app agree.' },
      { q: 'Do clients see their PRs?', a: 'Yes. The new-PR message appears in the latest version of the client app.' },
    ],
  },
  {
    slug: 'standing-class-bookings',
    name: 'Weekly class bookings',
    englishOnly: true,
    title: 'Recurring Class Bookings for Gyms: Book Every Week | TrainZilla',
    description:
      'Let gym members keep a spot in a class every week. Full classes use a waitlist, members can skip a date, and staff can add or remove members from the class roster.',
    keywords: 'recurring class booking gym software, book class every week, standing class booking, gym class waitlist software, gym class roster management',
    eyebrow: 'Gym classes',
    h1: 'Members book a class once and keep their spot every week',
    intro:
      'For classes like Monday and Wednesday HIIT, members should not have to rebook every week. A standing booking reserves the member’s place on every upcoming class, and the waitlist and roster work the same as for one-off bookings.',
    steps: [
      { title: 'Create the class', body: 'Set the days, time, instructor, location and capacity for a recurring class.' },
      { title: 'Book every week', body: 'A member chooses Book every week in the client app, or staff add them from the class roster, for all days or just some.' },
      { title: 'It stays up to date', body: 'Each upcoming class is booked automatically, a full class puts the member on the waitlist, and a member can skip one date or stop at any time.' },
    ],
    details: [
      'Staff can add or remove a member for a single class, or set up a standing booking from the roster',
      'Members with a lapsed membership cannot start a standing booking. Dates inside a paused membership are skipped',
      'Auto-renewing memberships keep their standing booking',
      'Staff cannot overbook a full class. Extra members go on the waitlist',
      'Class bookings do not use up session-pack sessions',
    ],
    plan: 'Included on gym plans.',
    related: [
      { label: 'Gyms and studios', to: '/gyms' },
      { label: 'Check-in kiosk', to: '/features/gym-check-in-kiosk' },
      { label: 'Session packs', to: '/features/session-packs' },
      { label: 'Gym management software', to: '/blog/gym-studio-management-software' },
    ],
    faq: [
      { q: 'What if a class is full?', a: 'The member joins the waitlist for that date and is moved into the class if a seat opens.' },
      { q: 'What happens when a membership ends?', a: 'Standing bookings run up to the membership end date, unless the membership auto-renews.' },
      { q: 'Can coaches manage this from the mobile apps?', a: 'Not yet. Roster changes and standing bookings are managed from the web dashboard; members book from the client app.' },
    ],
  },
  {
    slug: 'public-page-templates',
    name: 'Public page templates',
    englishOnly: true,
    title: 'Personal Trainer & Gym Website Templates | TrainZilla',
    description:
      'Every Trainzilla coach and gym gets a public page. Choose from four designs, preview them live, and add your own logo, name and colour on Coach Pro or a gym plan. Connect your own domain.',
    keywords: 'personal trainer website template, gym website builder, coach landing page, personal trainer profile page, gym page with own domain',
    eyebrow: 'Your online presence',
    h1: 'A personal trainer or gym website that comes with your account',
    intro:
      'Your public page lists your plans, programs, testimonials and transformations, with a pay link. Pick one of four designs and preview it before you apply, then make it yours with your logo and colours.',
    steps: [
      { title: 'Preview the designs', body: 'Kinetic, Clean, Studio and Boutique. Preview each on phone or desktop size before choosing.' },
      { title: 'Apply your brand', body: 'On Coach Pro or a gym plan, your logo, name and colour replace Trainzilla branding on the page.' },
      { title: 'Share it', body: 'Send the link, or connect your own domain. People who add the page to their phone’s home screen see your logo as the icon.' },
    ],
    details: [
      'Four templates, available to every coach and gym',
      'Your own logo, name and colour with a branded header and footer on Coach Pro and gym plans',
      'Gyms can add a photo gallery of up to 12 photos',
      'Testimonials, and transformations with several before-and-after photos',
      'Plans, programs and a pay link on the page',
      'Chosen and edited from the web dashboard',
    ],
    plan: 'Templates for every coach and gym. Your own branding and domain on Coach Pro and gym plans.',
    related: [
      { label: 'White label fitness app', to: '/white-label-fitness-app' },
      { label: 'Personal trainer website with a custom domain', to: '/blog/personal-trainer-website-custom-domain' },
      { label: 'Marketplace', to: '/marketplace' },
      { label: 'Pricing', to: '/pricing' },
    ],
    faq: [
      { q: 'Is the design choice free?', a: 'Yes. All four templates are available to every coach and gym. Adding your own logo, name and colour is part of Coach Pro and gym plans.' },
      { q: 'Can I use my own domain?', a: 'Yes, on Coach Pro and gym plans you can connect a domain you own and the page, with the template you chose, is served there.' },
      { q: 'Can I pick a template from the coach app?', a: 'Not yet. Choose it from the web dashboard.' },
    ],
  },
  {
    slug: 'direct-payments',
    name: 'Direct payments',
    englishOnly: true,
    title: 'Take Client Payments With No Commission | TrainZilla',
    description:
      'Clients pay you directly: UPI in India, or your own Stripe, PayPal or Wise link anywhere else. You confirm the payment and Trainzilla sets up the subscription and invoice. No commission.',
    keywords: 'coaching software no transaction fees, personal trainer accept upi payments, online coaching payments no commission, collect client payments stripe paypal wise, gym membership payments to owner',
    eyebrow: 'Payments',
    h1: 'Get paid straight to you. Trainzilla takes no cut.',
    intro:
      'Clients pay your UPI ID or your own payment link, so the money goes to you and not through our account. When you confirm a payment, Trainzilla creates the client’s subscription, records the payment and issues the invoice.',
    steps: [
      { title: 'Add your payment details', body: 'In India, add your UPI ID. Elsewhere, add your Stripe, PayPal or Wise link, plus any instructions for clients.' },
      { title: 'Client pays and tells you', body: 'The client pays on your link, then taps “I’ve paid” on your page with a payment reference.' },
      { title: 'You confirm', body: 'Confirm in Billing. Trainzilla creates the client account and subscription, records the payment and sends the invoice. Reject it and the client is told.' },
    ],
    details: [
      'No commission from Trainzilla on what clients pay you',
      'UPI in India. Your own Stripe, PayPal, Wise or other link outside India',
      'Gym plans pay the gym owner’s UPI ID or link',
      'Programs you sell work the same way as plans',
      'Direct payments do not auto-renew. You confirm each renewal payment',
      'Every confirmed payment gets an invoice, with GSTIN optional',
    ],
    plan: 'Included for every coach and gym.',
    related: [
      { label: 'Client invoices', to: '/features/client-invoices' },
      { label: 'Pricing', to: '/pricing' },
      { label: 'UPI payments guide', to: '/blog/upi-digital-payments-guide' },
      { label: 'Marketplace', to: '/marketplace' },
    ],
    faq: [
      { q: 'Does Trainzilla take a percentage?', a: 'No. With direct payments the client pays you, and Trainzilla charges only your subscription.' },
      { q: 'Why do I have to confirm payments?', a: 'The payment happens on your own account, so Trainzilla cannot see it. Your confirmation is what activates the client.' },
      { q: 'Can clients pay by card inside Trainzilla?', a: 'Coaches with bank details set up in India can also use Trainzilla checkout. Payment-gateway fees apply to those payments.' },
      { q: 'Do direct payments renew automatically?', a: 'No. The client pays again when the plan ends and you confirm it.' },
    ],
  },
  {
    slug: 'refer-a-coach',
    name: 'Refer a coach',
    englishOnly: true,
    title: 'Refer a Coach, Get a Free Month of Coach Pro | TrainZilla',
    description:
      'Share your Trainzilla link with another coach. They get their first month of Coach Pro free, and you get a free month of Coach Pro when they pay. No cash, no limit on referrals.',
    keywords: 'trainzilla referral, coaching software referral program, refer a coach, personal trainer software free month, coach pro referral',
    eyebrow: 'Referral program',
    h1: 'Refer a coach. You both get a free month of Coach Pro.',
    intro:
      'Know a coach still running clients on WhatsApp and spreadsheets? Send them your link. They start with their first month of Coach Pro free, and when they pay for Coach Pro or a gym plan, you get a free month too.',
    steps: [
      { title: 'Find your link', body: 'Open the Refer a coach card on your Trainzilla dashboard and copy your personal link.' },
      { title: 'They sign up', body: 'The coach you send signs up through your link and gets their first month of Coach Pro free: unlimited clients, no card.' },
      { title: 'You get a month', body: 'When they pay for Coach Pro or a gym plan, a free month of Coach Pro is added to your account.' },
    ],
    details: [
      'The coach you refer gets their first month of Coach Pro free, with unlimited clients and no card',
      'You get one free month of Coach Pro for each referred coach who pays',
      'No limit on how many coaches you refer',
      'Rewards are time on your plan. There is no cash payout',
      'The card shows how many coaches signed up, how many paid and how many months you have earned',
      'Available on the web dashboard for every coach',
    ],
    plan: 'Free for every coach.',
    related: [
      { label: 'Pricing', to: '/pricing' },
      { label: 'Compare coaching software', to: '/compare' },
      { label: 'Switch without losing clients', to: '/blog/switch-coaching-software-without-losing-clients' },
      { label: 'PDF plan import', to: '/features/pdf-plan-import' },
    ],
    faq: [
      { q: 'When do I get my free month?', a: 'When the coach you referred makes their first payment for Coach Pro or a gym plan. If you already pay for Coach Pro or are part of a gym, we apply the month to your account ourselves, which can take a little longer.' },
      { q: 'Does the person I refer need a card?', a: 'No. Their first month of Coach Pro is free with no card needed.' },
      { q: 'Can I refer myself?', a: 'No. Your own link, repeat sign-ups and inactive codes are ignored.' },
      { q: 'Is there a limit?', a: 'No. You earn a free month for every referred coach who pays.' },
      { q: 'Is it in the mobile apps?', a: 'The card is on the web dashboard now. The coach iPhone and Android apps are getting it in their next updates.' },
    ],
  },
  {
    slug: 'clients-without-the-app',
    name: 'Clients without the app',
    englishOnly: true,
    title: 'Coach Clients Who Never Install an App: WhatsApp Invites and a Private Client Link | TrainZilla',
    description:
      'Invite clients on WhatsApp without their email, or add a client who never installs the app. Share a private link to their plans, receipts and check-ins that opens on any phone with no login.',
    keywords: 'coach clients without app, whatsapp client invite link, personal trainer share plan whatsapp, client portal no login, add client without email, coaching software for clients who do not use apps',
    eyebrow: 'For clients who stay on WhatsApp',
    h1: 'Coach clients who never download the app',
    intro:
      'Plenty of clients will not install anything. Trainzilla lets you invite them on WhatsApp without knowing their email, or add them yourself, and then send one private link that shows their plans, receipts and check-ins in any browser.',
    steps: [
      { title: 'Invite on WhatsApp, or add them yourself', body: 'Enter a name and an optional number and Trainzilla builds the invite link. You send it from your own WhatsApp, text messages or copy it. Or choose “Add client without the app” and they go straight onto your roster.' },
      { title: 'Share their private client link', body: 'Open the client and choose “Share client link”. Trainzilla fills in the message and opens WhatsApp or your messages app. You press send from your own number.' },
      { title: 'They open it on any phone', body: 'No app and no login. The page shows their plans, receipts from you or your gym, and any check-in waiting for an answer, which they can fill in right there.' },
    ],
    details: [
      'Invite on WhatsApp with just a name. No email address needed, and nothing is emailed',
      'Add a client who will never install the app. You manage their plans, sessions and check-ins from your dashboard',
      'Clients added this way are never emailed, sent push notifications or messaged by workflow automations',
      'One private link per client: plans as branded PDFs, receipts, upcoming sessions and open check-ins',
      'Check-in answers they submit appear in your dashboard like any other check-in. Photo questions are skipped on the page',
      'They can open a day of their plan on the page and log workouts, meals and weight. Those logs reach you like app logs, PRs included',
      'Each no-app client card shows when their link was last opened and what they last logged. Nudge opens WhatsApp with a ready message and you press send',
      'The page carries your coach or gym branding. You can also record their body weight from their client screen',
      'Links work for 90 days by default, show how many times they were opened, and one tap revokes every link for a client',
      'Anyone who has the link can see that client’s page, so send it only to them. A new link is shown once, so make another if you lose it',
      'Removing a client switches their page off straight away',
    ],
    plan: 'Inviting and adding clients is included for every coach, and clients count toward your plan’s client limit. Plans on the client page are branded PDFs, which need Coach Pro.',
    related: [
      { label: 'Client invoices', to: '/features/client-invoices' },
      { label: 'PDF plan import', to: '/features/pdf-plan-import' },
      { label: 'Pricing', to: '/pricing' },
      { label: 'Switch without losing clients', to: '/blog/switch-coaching-software-without-losing-clients' },
    ],
    faq: [
      { q: 'Does the client need to install Trainzilla?', a: 'No. A client you add without the app only needs a browser. If they later want the app, you can invite them as usual.' },
      { q: 'Does Trainzilla message them?', a: 'No. Clients added without the app get no emails, push notifications or automated messages. You send each link yourself, from your own WhatsApp or messages app.' },
      { q: 'Is the link safe to send?', a: 'Anyone with the link can open that client’s page, and nothing else. It cannot be used to reach another client. Links expire, you can revoke them any time, and they stop working if you remove the client.' },
      { q: 'Do these clients count toward my limit?', a: 'Yes. They are clients on your roster, so your plan’s client limit applies.' },
      { q: 'Can they log workouts and meals without the app?', a: 'Yes. They open a day of their plan on their private page and log workouts, meals and weight. You see those logs in your dashboard, and PRs are flagged as usual.' },
      { q: 'Can they send me photos in a check-in?', a: 'Not yet. The page handles text, number and choice questions. Photo questions need the app.' },
      { q: 'Is it in the mobile apps?', a: 'It is on the web dashboard now. The coach iPhone and Android apps are getting it in their next updates.' },
    ],
  },
  {
    slug: 'workflow-automations',
    name: 'Workflow automations',
    englishOnly: true,
    title: 'Coaching Workflow Automations: Follow Up With Clients Automatically | TrainZilla',
    description:
      'Set rules that follow up with clients for you: a welcome message, a missed check-in nudge, a renewal reminder. Let AI draft a personal message and approve it before it sends. Coach Pro and gyms.',
    keywords: 'coaching workflow automation, automatic client follow up, personal trainer automation, missed check-in reminder, client re-engagement messages, gym automation software, ai drafted client messages',
    eyebrow: 'Coach Pro and gyms',
    h1: 'Follow up with every client without chasing them',
    intro:
      'A workflow is a rule: when something happens with a client, send a message or remind you. Start from a ready-made template, or let AI write a personal draft that waits for your approval before it goes out.',
    steps: [
      { title: 'Pick a template or build a rule', body: 'Choose when it runs (a new client connects, a check-in is missed, a plan is about to end), then what happens: a message to the client, a reminder to you, or an AI-written draft.' },
      { title: 'Review AI drafts', body: 'AI drafts land in your approvals inbox by default. Approve, edit or discard each one. Drafts you leave for 72 hours expire and are never sent.' },
      { title: 'Watch the activity log', body: 'See what ran, for which client and what was sent. Turn any workflow off in one tap.' },
    ],
    details: [
      'Skip clients: choose clients a workflow should leave out, and see how many each rule skips',
      'Eight triggers: a new client connects, a client reaches a milestone, a missed check-in, a client stops logging workouts, a plan about to end, a membership about to renew, a missed session, and a personal record',
      'Ready-made templates for welcome messages, missed check-in follow-up, re-engagement, plan ending, renewal reminders, no-show follow-up, PR celebrations and a 30-day milestone',
      'Send by in-app chat, push or email, or get a reminder yourself',
      'AI drafts a personal message and holds it for your approval. Switching approval off is your choice, per workflow',
      'Rule-based workflows are free. AI drafts use your AI Assistant credits, and Coach Pro includes a monthly allowance',
      'Messages go out between 8am and 9pm in the client’s own time zone, at most two workflow messages per client per day, and one run per event',
      'Clients can switch off automated coach messages in their app. Clients added without the app are never messaged',
      'Gyms can run workflows across every coach’s clients. Each message is sent by the client’s own coach, with the same limits',
      'If Coach Pro lapses, workflows pause and are kept, never deleted',
    ],
    plan: 'Coach Pro and gym plans.',
    related: [
      { label: 'Clients without the app', to: '/features/clients-without-the-app' },
      { label: 'AI assistant', to: '/features/ai-assistant' },
      { label: 'Pricing', to: '/pricing' },
      { label: 'Gyms', to: '/gyms' },
    ],
    faq: [
      { q: 'Will AI message my clients without me seeing it?', a: 'Not unless you turn approval off for that workflow. By default every AI draft waits in your approvals inbox and expires after 72 hours if you do nothing.' },
      { q: 'Can I stop it from spamming a client?', a: 'Yes. Workflow messages are limited to two per client per day and only sent between 8am and 9pm in the client’s time zone. Clients can also opt out in their app.' },
      { q: 'What does it cost?', a: 'Rule-based workflows cost nothing extra. AI drafts use AI Assistant credits from your wallet, and Coach Pro includes a monthly allowance.' },
      { q: 'Does it work for gyms?', a: 'Yes. A gym owner or admin can set workflows that run for every coach’s clients. Each message is sent by that client’s own coach.' },
      { q: 'Can I use it from Claude?', a: 'Yes. The Trainzilla MCP server can list, create and switch workflows, and review AI drafts, with every change previewed first.' },
      { q: 'Is it in the mobile apps?', a: 'It is on the web dashboard, and the coach iPhone and Android apps are getting it in their next updates.' },
    ],
  },
  {
    slug: 'switch-with-ai',
    name: 'Switch with AI',
    englishOnly: true,
    title: 'Switch to Trainzilla With AI: Move Your Clients From Any Platform | TrainZilla',
    description:
      'Moving from Trainerize, Everfit, TrueCoach, a spreadsheet or WhatsApp? Tell Claude or the Trainzilla AI Assistant what you have. It reads it, maps it, and invites your clients once you approve the preview.',
    keywords: 'switch to trainzilla, move clients from trainerize, migrate from everfit, truecoach alternative migration, import clients personal trainer app, switch coaching platform, ai client import, import clients from spreadsheet',
    eyebrow: 'Coach Pro',
    h1: 'Move your coaching business to Trainzilla by chatting with AI',
    intro:
      'No importer to learn and no column mapping. Give an AI assistant whatever you have (an export, a pasted client list, a screenshot, plan PDFs, even a typed list) and it works out the rest. You see a preview of every client before a single invite is sent.',
    steps: [
      { title: '1. Connect Claude to Trainzilla (or use the built-in AI Assistant)', body: 'Either add the Trainzilla connector in Claude (Settings → Customize → Connectors, custom connector, https://api.tzilla.live/mcp, then sign in to Trainzilla), or open the AI Assistant inside your Trainzilla dashboard, which needs no setup. Bulk invites need Coach Pro; the 30-day trial is limited to one client.' },
      { title: '2. Say you are switching', body: 'Type something like "I am moving my clients from Trainerize to Trainzilla" and say what you can share. If your Claude lists connector prompts, you can pick the switch_to_trainzilla prompt instead; the result is the same.' },
      { title: '3. Hand over what you have', body: 'Any of these work: a CSV or Excel export, a table copied from the old app\'s client list and pasted into the chat, a screenshot of the list, an export from your payment tool or phone contacts, or just a typed list. Partial is fine. Email is the only required detail per client; name, phone, goals and injuries are picked up when present. Program and meal-plan PDFs can come along too.' },
      { title: '4. Check the table AI builds', body: 'It maps the columns itself, joins first and last names, lowercases and de-duplicates emails, and builds one private note per client from goals, injuries, tags or start date. It lists clients with no email (they can join from an invite link you share) and anything it could not read. Anything read from a picture is shown back so you can fix typos first.' },
      { title: '5. Try two or three clients first', body: 'AI asks whether to start with a small test batch. It runs a dry run of the invites and shows exactly who would be emailed. Nothing is sent until you reply yes.' },
      { title: '6. Send the invites', body: 'After your yes, invites go out in batches of up to 200. You get every row\'s outcome: sent, already invited, duplicate, invalid email or failed. Running the same list again is safe, because duplicates and pending invites are skipped.' },
      { title: '7. Bring over plans', body: 'From PDFs or pasted text, AI builds workout and nutrition templates in your wording, rep ranges such as 8-10 included. It previews each one and saves only what you confirm. You assign them to clients once they have joined.' },
      { title: '8. Add history when clients have joined', body: 'For clients who accepted, AI can add back-dated weights, measurements and photos, and past check-ins with their answers, but only data you actually gave it, with the exact dates. The private note you attached to an invite is saved to your client notes the moment the client connects.' },
      { title: '9. Wrap up', body: 'AI summarises what moved, which invites are still pending, what could not move, and the next step: ask clients to open the invite email and install the free app.' },
    ],
    details: [
      'Works from whatever you have: CSV, Excel, pasted text, screenshots, plan PDFs or a typed list. Nothing assumes a particular platform\'s export layout',
      'If the old platform has no export, you can copy the client list from its screen, send screenshots, use the same people from your payment tool, phone contacts or a WhatsApp group, or type up to about 30 clients',
      'Optional, only if your Claude has a browser: you sign in to the old platform yourself, and AI reads just the client list on screen. It never sees your password, never clicks anything that edits, deletes or sends, and stops at any sign-in, 2FA or CAPTCHA',
      'Every write is confirm-gated: a plain-language preview first, your explicit yes second. Invites send real emails, so the preview matters',
      'Invites carry an optional private note per client, kept off the invite and saved to your coach notes when the client connects',
      'Plans become editable Trainzilla templates, not locked files. Rep ranges and your exercise wording are kept',
      'Back-dated progress and past check-ins can be loaded for clients who have joined; progress for a client who has not finished setup waits and attaches automatically',
      'What cannot move: anything you cannot export, paste, photograph or type. Old workout logs and progress photos come across only if you have them as files or text, so keep the old account read-only for a month. Payments and subscriptions with the old platform are not touched; you cancel those yourself',
      'Clients exist in Trainzilla only after they accept the invite',
    ],
    plan: 'Bulk invite needs Coach Pro. Connecting Claude uses your own Claude plan. The in-app AI Assistant uses your AI Assistant credits, and Coach Pro includes a monthly allowance.',
    related: [
      { label: 'Switching from Trainerize', to: '/switch/trainerize' },
      { label: 'Switching from Everfit', to: '/switch/everfit' },
      { label: 'Switching from TrueCoach', to: '/switch/truecoach' },
      { label: 'Plans from PDF', to: '/features/pdf-plan-import' },
      { label: 'Connect Claude (MCP server)', to: '/features/mcp-server' },
      { label: 'Pricing', to: '/pricing' },
    ],
    faq: [
      { q: 'Which platforms can I switch from?', a: 'Any. The AI works from what you hand it, not from a built-in importer for named tools, so Trainerize, Everfit, TrueCoach, PT Distinction, Excel, Google Sheets and WhatsApp lists all work the same way.' },
      { q: 'Will it email my clients without asking me?', a: 'No. It shows a table and a dry run first and only sends after you say yes. Starting with two or three test clients is built into the flow.' },
      { q: 'What if my old platform has no export?', a: 'Copy the client list from the screen and paste it, send screenshots, or use the same people from your payment tool, contacts or a WhatsApp group. For a few clients you can type them. The AI turns any of these into the same client table.' },
      { q: 'Does it log in to my old account?', a: 'Only if you ask and your Claude has a browser, and you sign in yourself. It never sees or types your password or 2FA code, and reads the client list only. Pasting or screenshots are the simpler routes.' },
      { q: 'Can it move old workout logs and photos?', a: 'Only what you can give it as files or text, such as a few key weights or start dates per client. Otherwise clients start fresh in Trainzilla. Keep the old account open read-only for a month.' },
      { q: 'Do my clients need to do anything?', a: 'Each client opens the invite email and installs the free Trainzilla app. They appear in your roster once they accept.' },
      { q: 'Does it work on the free trial?', a: 'You can try the flow with one client. Inviting your whole list needs Coach Pro, which is $19 a month for unlimited clients outside India.' },
      { q: 'Can you do it for me?', a: 'Yes. Book a free switch-over call and we do it together; the AI route is for coaches who want to move at their own pace.' },
    ],
  },
];

export function getFeaturePage(slug: string): FeaturePage | undefined {
  return FEATURE_PAGES.find((f) => f.slug === slug);
}
